import { API_URL, authenticatedFetch, parseApiErrorMessage } from "../config/api";

// Login interfaces
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

export interface RefreshTokenRequest {
  refresh_token: string;
}

// Registration interfaces
export interface RegisterUserData {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  username: string;
}

export interface RegisterUserResponse {
  id: string;
}

export interface UserInterface {
  id: string;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_email_verified: boolean;
  created_at: string;
  updated_at: string | null;
  active_to: string;
}

/**
 * Login user with email and password
 * NOTE: Uses regular fetch - not authenticated
 */
export const loginUser = async (credentials: LoginCredentials): Promise<LoginResponse> => {
  try {
    const response = await fetch(`${API_URL}/users/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Login failed' }));
      throw new Error(errorData.message || 'Login failed');
    }

    const data: LoginResponse = await response.json();
    return data;
  } catch (error: Error | any) {
    throw new Error(error.message || 'Login failed');
  }
};

/**
 * Refresh access token using refresh token
 * NOTE: Uses regular fetch - not authenticated
 */
export const refreshAccessToken = async (refreshToken: string): Promise<LoginResponse> => {
  try {
    const response = await fetch(`${API_URL}/users/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${refreshToken}`,
      },
      body: JSON.stringify({ refresh_token: refreshToken })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Token refresh failed' }));
      throw new Error(errorData.message || 'Token refresh failed');
    }

    const data: LoginResponse = await response.json();
    return data;
  } catch (error: Error | any) {
    throw new Error(error.message || 'Token refresh failed');
  }
};

/**
 * Register new user
 * NOTE: Uses regular fetch - not authenticated
 */
export const registerUser = async (userData: RegisterUserData): Promise<RegisterUserResponse> => {
  try {
    const response = await fetch(`${API_URL}/users/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData)
    });

    if (!response.ok) {
      throw new Error(await parseApiErrorMessage(response, 'Registration failed'));
    }

    const data: RegisterUserResponse = await response.json();
    return data;
  } catch (error: Error | any) {
    throw new Error(error.message || 'Registration failed');
  }
};

export const getUsers = async (_token: string): Promise<ProjectUsersInterface> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/users/?size=1000&t=${new Date().getTime()}`);
    const data:ProjectUsersInterface = await r.json();
    return data;
  } catch(e: Error | any) {
    throw new Error(e);
  }
};

/**
 * Create new user
 */
export const createUser = async (_token: string, userData: any): Promise<any> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/users/`, {
      method: 'POST',
      body: JSON.stringify(userData)
    });

    if (!r.ok) {
      throw new Error(await parseApiErrorMessage(r, 'Failed to create user'));
    }

    const data = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to create user');
  }
};

/**
 * Get user profile
 */
export const getUserProfile = async (_token: string, userId: string): Promise<UserProfile> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/users/${userId}/profile`);

    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: UserProfile = await r.json();
    console.log('📥 getUserProfile: Raw response:', JSON.stringify(data, null, 2));
    console.log('📥 getUserProfile: Skills type:', typeof data.skills, 'Value:', data.skills);
    console.log('📥 getUserProfile: Is array?', Array.isArray(data.skills), 'Length:', data.skills?.length);
    return data;
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to get user profile');
  }
};

/**
 * Create user profile (for new users)
 */
export const createUserProfile = async (
  _token: string, 
  userId: string, 
  profileData: UserProfileFormData
): Promise<UserProfile> => {
  try {
    const requestBody = {
      id: userId,
      ...profileData,
      skills: Array.isArray(profileData.skills) && profileData.skills.length > 0
        ? profileData.skills
        : null
    };

    console.log('➕ createUserProfile: Request body:', JSON.stringify(requestBody, null, 2));

    const r = await authenticatedFetch(`${API_URL}/users/${userId}/profile`, {
      method: 'POST',
      body: JSON.stringify(requestBody)
    });

    if (!r.ok) {
      const errorText = await r.text();
      console.error('❌ createUserProfile: API error', r.status, errorText);
      throw new Error(`HTTP ${r.status}: ${errorText || r.statusText}`);
    }

    if (r.status === 204) {
      return {
        id: userId,
        ...profileData
      } as UserProfile;
    }

    const data: UserProfile = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to create user profile');
  }
};

/**
 * Update user profile
 */
export const updateUserProfile = async (
  _token: string, 
  userId: string, 
  profileData: UserProfileFormData
): Promise<UserProfile> => {
  try {
    const requestBody = {
      id: userId,
      ...profileData,
      skills: Array.isArray(profileData.skills) && profileData.skills.length > 0
        ? profileData.skills
        : null
    };

    console.log('🔄 updateUserProfile: Request body:', JSON.stringify(requestBody, null, 2));

    const r = await authenticatedFetch(`${API_URL}/users/${userId}/profile`, {
      method: 'PUT',
      body: JSON.stringify(requestBody)
    });

    if (!r.ok) {
      const errorText = await r.text();
      console.error('❌ updateUserProfile: API error', r.status, errorText);
      throw new Error(`HTTP ${r.status}: ${errorText || r.statusText}`);
    }

    if (r.status === 204) {
      return {
        id: userId,
        ...profileData
      } as UserProfile;
    }

    const data: UserProfile = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to update user profile');
  }
};

/**
 * Save user profile (create or update automatically)
 */
export const saveUserProfile = async (
  token: string, 
  userId: string, 
  profileData: UserProfileFormData,
  isNewProfile: boolean = false
): Promise<UserProfile> => {
  try {
    if (!isNewProfile) {
      try {
        return await updateUserProfile(token, userId, profileData);
      } catch (error) {
        if (error instanceof Error && (error.message.includes('404') || error.message.includes('422'))) {
          console.log('🔄 Profile update failed, trying create...');
          return await createUserProfile(token, userId, profileData);
        }
        throw error;
      }
    } else {
      return await createUserProfile(token, userId, profileData);
    }
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to save user profile');
  }
};

/**
 * Get user accounts
 */
export const getUserAccounts = async (_token: string, userId: string): Promise<UserAccount[]> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/users/${userId}/accounts`);

    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: UserAccount[] = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to get user accounts');
  }
};

/**
 * Create user account
 */
export const createUserAccount = async (
  _token: string, 
  userId: string, 
  accountData: UserAccountFormData
): Promise<UserAccount> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/users/${userId}/accounts`, {
      method: 'POST',
      body: JSON.stringify(accountData)
    });

    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: UserAccount = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to create user account');
  }
};

/**
 * Get account details
 */
export const getAccountDetails = async (
  _token: string, 
  userId: string, 
  accountId: string
): Promise<UserAccount> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/users/${userId}/accounts/${accountId}`);

    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: UserAccount = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to get account details');
  }
};

/**
 * Add team member to account
 */
export const addTeamMember = async (
  _token: string, 
  userId: string, 
  accountId: string, 
  memberId: string
): Promise<void> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/users/${userId}/accounts/${accountId}/users/${memberId}`, {
      method: 'POST'
    });

    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to add team member');
  }
};

/**
 * Remove team member from account
 */
export const removeTeamMember = async (
  _token: string, 
  userId: string, 
  accountId: string, 
  memberId: string
): Promise<void> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/users/${userId}/accounts/${accountId}/users/${memberId}`, {
      method: 'DELETE'
    });

    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to remove team member');
  }
};

/**
 * Get available account roles
 */
export const getAvailableRoles = async (_token: string, userId: string): Promise<UserRole[]> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/users/${userId}/accounts/roles`);

    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: UserRole[] = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to get available roles');
  }
};

/**
 * Upload user document
 */
export const uploadUserDocument = async (
  _token: string,
  userId: string,
  documentType: string,
  file: File
): Promise<{url: string}> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const r = await authenticatedFetch(`${API_URL}/users/${userId}/profile/documents/${documentType}`, {
      method: 'POST',
      body: formData
    });

    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data = await r.json();
    return data;
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to upload user document');
  }
};

/**
 * Delete user document
 */
export const deleteUserDocument = async (
  _token: string,
  userId: string,
  documentType: string
): Promise<void> => {
  try {
    const r = await authenticatedFetch(`${API_URL}/users/${userId}/profile/documents/${documentType}`, {
      method: 'DELETE'
    });

    if (!r.ok) {
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }
  } catch (e: Error | any) {
    throw new Error(e.message || 'Failed to delete user document');
  }
};

// Forgot Password interfaces
export interface ForgotPasswordData {
  email: string;
}

export interface ForgotPasswordResponse {
  message: string;
}

/**
 * Send forgot password email
 * NOTE: Uses regular fetch - not authenticated
 */
export const forgotPassword = async (emailData: ForgotPasswordData): Promise<ForgotPasswordResponse> => {
  try {
    const response = await fetch(`${API_URL}/users/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(emailData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to send password reset email');
    }

    const data: ForgotPasswordResponse = await response.json();
    return data;
  } catch (error: Error | any) {
    throw new Error(error.message || 'Failed to send password reset email');
  }
};

// Resend Invitation interfaces
export interface ResendInvitationData {
  email: string;
}

export interface ResendInvitationResponse {
  message: string;
}

/**
 * Resend invitation email to user
 */
export const resendInvitation = async (
  _token: string,
  emailData: ResendInvitationData
): Promise<ResendInvitationResponse> => {
  try {
    const response = await authenticatedFetch(`${API_URL}/users/resend-confirmation-code`, {
      method: 'POST',
      body: JSON.stringify(emailData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to resend invitation');
    }

    const data: ResendInvitationResponse = await response.json();
    return data;
  } catch (error: Error | any) {
    throw new Error(error.message || 'Failed to resend invitation');
  }
};
